import Autocomplete from '@mui/material/Autocomplete'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form'

export type Option = { id: string; label: string; hint?: string }

type Common<T extends FieldValues> = {
  /** `any` for the context and transformed-value generics: these fields are used with
   *  forms whose zod output type differs from their input type. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<T, any, any>
  name: Path<T>
  label: string
  disabled?: boolean
  helperText?: string
}

/** Searchable single-select bound to a string id; `''` means "no selection". */
export function EntitySelect<T extends FieldValues>({
  control,
  name,
  label,
  options,
  loading,
  disabled,
  helperText,
  emptyLabel = 'ללא',
}: Common<T> & { options: Option[]; loading?: boolean; emptyLabel?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selected = options.find((o) => o.id === field.value) ?? null
        return (
          <Autocomplete
            options={options}
            value={selected}
            loading={loading}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_event, option) => field.onChange(option?.id ?? '')}
            noOptionsText="אין תוצאות"
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={emptyLabel}
                inputRef={field.ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? helperText}
              />
            )}
          />
        )
      }}
    />
  )
}

/** Fixed-choice select for enums. */
export function EnumSelect<T extends FieldValues>({
  control,
  name,
  label,
  options,
  disabled,
  helperText,
  allowEmpty,
  emptyLabel = 'הכל',
}: Common<T> & {
  options: Option[]
  allowEmpty?: boolean
  emptyLabel?: string
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          select
          label={label}
          disabled={disabled}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? helperText}
        >
          {allowEmpty ? <MenuItem value="">{emptyLabel}</MenuItem> : null}
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  )
}

/** Always `DD/MM/YYYY` on screen — the native date input follows the browser's
 *  locale instead, which is why this is a picker and not `<input type="date">`. */
const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY'

const toDayjs = (value: string | null | undefined): Dayjs | null => (value ? dayjs(value) : null)
const fromDayjs = (value: Dayjs | null): string =>
  value && value.isValid() ? value.format('YYYY-MM-DD') : ''

/** Calendar-day picker bound to the `YYYY-MM-DD` string the API expects. */
export function DateField<T extends FieldValues>({
  control,
  name,
  label,
  disabled,
  helperText,
}: Common<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <DatePicker
          label={label}
          format={DATE_DISPLAY_FORMAT}
          disabled={disabled}
          value={toDayjs(field.value as string | null | undefined)}
          onChange={(next) => field.onChange(fromDayjs(next))}
          inputRef={field.ref}
          slotProps={{
            textField: {
              onBlur: field.onBlur,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ?? helperText,
            },
          }}
        />
      )}
    />
  )
}

/** The same picker for plain state: `value` is `''` or `YYYY-MM-DD`. */
export function DateInput({
  label,
  value,
  onChange,
  disabled,
  helperText,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  helperText?: string
}) {
  return (
    <DatePicker
      label={label}
      format={DATE_DISPLAY_FORMAT}
      disabled={disabled}
      value={toDayjs(value)}
      onChange={(next) => onChange(fromDayjs(next))}
      slotProps={{ textField: { helperText } }}
    />
  )
}
